import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Text,
  toast,
} from '@kognitos/lattice';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
} from '@/shared/stores/user-preferences.store';
import { WorkspaceIconPicker } from '@/shared/components/WorkspaceIconPicker';
import { useAuth } from '@/shared/auth';
import {
  getCurrentUserOrganizationWorkspacesQueryKey,
  getListWorkspacesQueryKey,
  useCreateWorkspace,
} from '@/generated/hooks/agora/v1/workspace';
import { useGetOrganization } from '@/generated/hooks/agora/v1/organization';
import {
  WorkspaceColor,
  WorkspaceIcon,
  type WorkspaceIconType,
} from '@/modules/onboarding/constants';
import { captureException } from '@/modules/sentry';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';
import { AccessDeniedAlert } from '@/shared/components';
import {
  createWorkspaceSchema,
  type CreateWorkspaceFormData,
} from '../create-workspace-schema';
import { useNavigate } from 'react-router-dom';
import { getRoutePath } from '@/routes/util';
import { parseResourceName } from '@/shared/utils';

interface ICreateWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateWorkspaceDialog({
  isOpen,
  onClose,
}: ICreateWorkspaceDialogProps) {
  const { t } = useTranslation('workspace');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const form = useForm<CreateWorkspaceFormData>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: {
      workspaceName: '',
      workspaceIcon: WorkspaceIcon.GRIP,
      workspaceColor: WorkspaceColor.CYAN,
    },
  });
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const { data: organizationData } = useGetOrganization(currentOrganizationId, {
    query: {
      enabled: Boolean(currentOrganizationId),
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      retry: false,
    },
  });

  const {
    mutate: createWorkspace,
    isPending: isCreatingWorkspace,
    error: createWorkspaceError,
    reset: resetCreateWorkspace,
  } = useCreateWorkspace();

  const isAccessDeniedError = checkIsAccessDeniedError(createWorkspaceError);

  const onSubmit = (data: CreateWorkspaceFormData) => {
    createWorkspace(
      {
        organization: currentOrganizationId,
        data: {
          name: data.workspaceName,
          title: data.workspaceName,
          workspace_icon: data.workspaceIcon,
          workspace_icon_color: data.workspaceColor,
        },
      },
      {
        onSuccess: newWorkspace => {
          onClose();
          queryClient.invalidateQueries({
            queryKey: getCurrentUserOrganizationWorkspacesQueryKey(
              currentOrganizationId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListWorkspacesQueryKey(currentOrganizationId),
          });

          if (newWorkspace.data.name) {
            const workspaceId = parseResourceName(
              newWorkspace.data.name,
            ).workspace;
            if (workspaceId) {
              navigate(
                getRoutePath('AUTOMATIONS', {
                  orgId: currentOrganizationId,
                  workspaceId,
                }),
              );
            }
          }
          toast.success(t('workspace.create.success'));
        },
        onError: error => {
          captureException(error);
        },
      },
    );
  };

  // eslint-disable-next-line no-restricted-syntax -- reason: Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      form.reset();
      resetCreateWorkspace();
    }
  }, [isOpen, form, resetCreateWorkspace]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('workspace.create.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('workspace.create.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            id="create-workspace-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <Flex gap={3} align="start">
              <div className="flex flex-col">
                <FormField
                  control={form.control}
                  name="workspaceIcon"
                  render={({ field: iconField }) => (
                    <FormField
                      control={form.control}
                      name="workspaceColor"
                      render={({ field: colorField }) => (
                        <FormItem>
                          <FormControl>
                            <WorkspaceIconPicker
                              selectedIcon={
                                (iconField.value ||
                                  WorkspaceIcon.GRIP) as WorkspaceIconType
                              }
                              selectedColor={
                                colorField.value || WorkspaceColor.CYAN
                              }
                              onIconChange={iconField.onChange}
                              onColorChange={colorField.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="workspaceName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t('workspace.create.label')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('workspace.create.placeholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Flex>
            <div>
              <Text level="small" color="muted">
                {t('workspace.create.owner')}
              </Text>
              <Text weight="semibold">{user?.name}</Text>
            </div>

            <div>
              <Text level="small" color="muted">
                {t('workspace.create.organization')}
              </Text>
              <Text weight="semibold">{organizationData?.data?.title}</Text>
            </div>
          </form>
          {createWorkspaceError &&
            (isAccessDeniedError ? (
              <AccessDeniedAlert />
            ) : (
              <Alert variant="destructive">
                <Icon type="AlertCircle" />
                <AlertDescription>
                  {t('workspace.create.error.description')}
                </AlertDescription>
              </Alert>
            ))}
        </Form>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCreatingWorkspace}
          >
            {t('workspace.create.cancel')}
          </Button>
          <Button
            type="submit"
            form="create-workspace-form"
            isLoading={isCreatingWorkspace}
            disabled={isCreatingWorkspace}
          >
            {t('workspace.create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
